<template>
  <div class="carrier-analytics">
    <!-- Header Section -->
    <div class="header-section">
      <div class="container">
        <h1 class="main-title">
          <i class="fas fa-building"></i>
          Carrier Analytics Dashboard
        </h1>
        <p class="subtitle">Deep dive into carrier performance, policy distribution, premiums, and insured entities</p>
        <div class="controls">
          <div class="project-selector">
            <label for="project-select">
              <i class="fas fa-folder"></i>
              Select Project:
            </label>
            <select 
              id="project-select"
              v-model="projectId" 
              class="project-select"
              :disabled="loading || loadingProjects"
            >
              <option value="" disabled>{{ loadingProjects ? 'Loading projects...' : 'Choose a project' }}</option>
              <option 
                v-for="project in projects" 
                :key="project.id" 
                :value="project.id"
              >
                {{ project.name }} (ID: {{ project.id }})
              </option>
            </select>
          </div>
          <button @click="loadResults" :disabled="loading || !projectId" class="load-btn">
            <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
            <span v-if="loading">Loading...</span>
            <span v-else>Load Data</span>
          </button>
          <button v-if="!loading && results.length > 0" @click="exportData" class="export-btn">
            <i class="fas fa-file-export"></i>
            Export Carrier CSV
          </button>
          <button v-if="!loading && results.length > 0" @click="exportInsuredMatrix" class="export-btn">
            <i class="fas fa-table"></i>
            Export Insured Matrix
          </button>
          <button v-if="!loading && results.length > 0" @click="exportPolicyTypeDiagnostic" class="export-btn" style="background: #9333ea;">
            <i class="fas fa-bug"></i>
            Debug Policy Types
          </button>
          <button v-if="!loading && results.length > 0" @click="exportMissingCertificates" class="export-btn" style="background: #dc2626;">
            <i class="fas fa-exclamation-triangle"></i>
            Missing Certificates
          </button>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="container">
      <div class="notification error">
        <i class="fas fa-exclamation-circle"></i>
        {{ error }}
      </div>
    </div>

    <!-- Main Content -->
    <div v-if="!loading && results.length > 0" class="dashboard-section">
      <div class="container">
        
        <!-- Summary Metrics Bar -->
        <div class="metrics-bar">
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-file-alt"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ summary.total_documents }}</div>
              <div class="metric-label">Total Policies</div>
            </div>
          </div>
          <div v-if="failedExtractions.length > 0" class="metric-card metric-warning">
            <div class="metric-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ failedExtractions.length }}</div>
              <div class="metric-label">Failed Extractions</div>
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-building"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ Object.keys(carrierStats).length }}</div>
              <div class="metric-label">Carriers</div>
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-dollar-sign"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ formatCurrency(totalPremium) }}</div>
              <div class="metric-label">Total Premium</div>
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-users"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ uniqueInsureds }}</div>
              <div class="metric-label">Unique Insureds</div>
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-store"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ uniqueStores }}</div>
              <div class="metric-label">Unique Stores</div>
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ formatCurrency(averagePremium) }}</div>
              <div class="metric-label">Avg Premium</div>
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-icon">
              <i class="fas fa-shield-alt"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ formatCurrency(totalLiabilityExposure) }}</div>
              <div class="metric-label">Total Liability Exposure</div>
            </div>
          </div>
        </div>

        <!-- Search and Filter -->
        <div class="filter-section">
          <div class="search-box">
            <i class="fas fa-search"></i>
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search carriers..."
              class="search-input"
            />
          </div>
          <div class="filter-controls">
            <select v-model="sortBy" class="sort-select">
              <option value="premium">Sort by Premium</option>
              <option value="policies">Sort by Policy Count</option>
              <option value="name">Sort by Name</option>
              <option value="avgPremium">Sort by Avg Premium</option>
            </select>
            <select v-model="sortOrder" class="sort-select">
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        <!-- Carriers List with Expandable Details -->
        <div class="carriers-list">
          <div 
            v-for="carrier in filteredAndSortedCarriers" 
            :key="carrier.name" 
            class="carrier-card"
            :class="{ 'expanded': expandedCarrier === carrier.name }"
          >
            <!-- Carrier Header -->
            <div class="carrier-header" @click="toggleCarrier(carrier.name)">
              <div class="carrier-info">
                <div class="carrier-name">
                  <i class="fas fa-building"></i>
                  {{ carrier.name }}
                </div>
                <div class="carrier-meta">
                  <span v-if="carrier.naic" class="meta-badge">
                    <i class="fas fa-hashtag"></i>
                    NAIC: {{ carrier.naic }}
                  </span>
                  <span v-if="carrier.amBest" class="meta-badge rating">
                    <i class="fas fa-star"></i>
                    A.M. Best: {{ carrier.amBest }}
                  </span>
                </div>
              </div>
              
              <div class="carrier-summary">
                <div class="summary-stat">
                  <div class="stat-value">{{ carrier.policyCount }}</div>
                  <div class="stat-label">Policies</div>
                </div>
                <div class="summary-stat">
                  <div class="stat-value">{{ formatCurrency(carrier.totalPremium) }}</div>
                  <div class="stat-label">Total Premium</div>
                </div>
                <div class="summary-stat">
                  <div class="stat-value">{{ formatCurrency(carrier.avgPremium) }}</div>
                  <div class="stat-label">Avg Premium</div>
                </div>
                <div class="summary-stat">
                  <div class="stat-value">{{ formatCurrency(carrier.totalAggregate) }}</div>
                  <div class="stat-label">Total Exposure</div>
                </div>
                <div class="summary-stat">
                  <div class="stat-value">{{ formatCurrency(carrier.avgAggregate) }}</div>
                  <div class="stat-label">Avg Limit</div>
                </div>
                <div class="summary-stat">
                  <div class="stat-value">{{ carrier.uniqueInsureds }}</div>
                  <div class="stat-label">Insureds</div>
                </div>
                <div class="expand-icon">
                  <i class="fas" :class="expandedCarrier === carrier.name ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </div>
              </div>
            </div>

            <!-- Carrier Details (Expandable) -->
            <transition name="slide">
              <div v-if="expandedCarrier === carrier.name" class="carrier-details">
                
                <!-- Policy Types Breakdown -->
                <div class="detail-section">
                  <h4>
                    <i class="fas fa-clipboard-list"></i>
                    Policy Types Breakdown
                  </h4>
                  <div class="policy-types-grid">
                    <div 
                      v-for="policyType in carrier.policyTypes" 
                      :key="policyType.type"
                      class="policy-type-card"
                    >
                      <div class="policy-type-header">
                        <div class="policy-type-name">{{ formatPolicyType(policyType.type) }}</div>
                        <div class="policy-type-count">{{ policyType.count }} policies</div>
                      </div>
                      <div class="policy-type-stats">
                        <div class="stat-row">
                          <span class="stat-label">Total Premium:</span>
                          <span class="stat-value">{{ formatCurrency(policyType.totalPremium) }}</span>
                        </div>
                        <div class="stat-row">
                          <span class="stat-label">Avg Premium:</span>
                          <span class="stat-value">{{ formatCurrency(policyType.avgPremium) }}</span>
                        </div>
                        <div class="stat-row">
                          <span class="stat-label">Total Aggregate Limit:</span>
                          <span class="stat-value">{{ formatCurrency(policyType.totalAggregate) }}</span>
                        </div>
                        <div class="stat-row">
                          <span class="stat-label">Avg Aggregate Limit:</span>
                          <span class="stat-value">{{ formatCurrency(policyType.avgAggregate) }}</span>
                        </div>
                        <div class="stat-row">
                          <span class="stat-label">Total Per Occurrence:</span>
                          <span class="stat-value">{{ formatCurrency(policyType.totalPerOccurrence) }}</span>
                        </div>
                        <div class="stat-row">
                          <span class="stat-label">Avg Per Occurrence:</span>
                          <span class="stat-value">{{ formatCurrency(policyType.avgPerOccurrence) }}</span>
                        </div>
                      </div>
                      
                      <!-- Policy Documents List -->
                      <div class="policy-documents">
                        <button 
                          @click="togglePolicyDocs(carrier.name, policyType.type)"
                          class="toggle-docs-btn"
                        >
                          <i class="fas" :class="showingDocs[`${carrier.name}-${policyType.type}`] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                          {{ showingDocs[`${carrier.name}-${policyType.type}`] ? 'Hide' : 'Show' }} Documents
                        </button>
                        <transition name="slide">
                          <div v-if="showingDocs[`${carrier.name}-${policyType.type}`]" class="documents-list">
                            <div 
                              v-for="doc in policyType.documents" 
                              :key="doc.id"
                              class="document-item"
                            >
                              <div class="doc-info">
                                <i class="fas fa-file-pdf"></i>
                                <span class="doc-name">{{ doc.document_name }}</span>
                              </div>
                              <div class="doc-meta">
                                <span v-if="doc.insured" class="insured-name">{{ doc.insured }}</span>
                                <span v-if="doc.premium" class="premium-amt">{{ formatCurrency(doc.premium) }}</span>
                                <span v-if="doc.policy_number" class="policy-num">{{ doc.policy_number }}</span>
                              </div>
                              <div class="doc-actions">
                                <button 
                                  @click="openDocument(doc.id)" 
                                  class="open-doc-btn"
                                  :disabled="!doc.id"
                                  title="Open document"
                                >
                                  <i class="fas fa-external-link-alt"></i>
                                  Open
                                </button>
                              </div>
                            </div>
                          </div>
                        </transition>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Insureds List -->
                <div class="detail-section">
                  <h4>
                    <i class="fas fa-users"></i>
                    Insureds ({{ carrier.insureds.length }})
                  </h4>
                  <div class="insureds-list">
                    <div v-for="insured in carrier.insureds.slice(0, showAllInsureds[carrier.name] ? undefined : 10)" :key="insured.name" class="insured-item">
                      <div class="insured-name">
                        {{ insured.name }}
                        <span v-if="insured.storeCount > 0" class="store-badge">{{ insured.storeCount }} store{{ insured.storeCount !== 1 ? 's' : '' }}</span>
                      </div>
                      <div class="insured-stats">
                        <span class="insured-policies">{{ insured.count }} policies</span>
                        <span class="insured-premium">{{ formatCurrency(insured.totalPremium) }}</span>
                      </div>
                    </div>
                    <button 
                      v-if="carrier.insureds.length > 10" 
                      @click="toggleShowAllInsureds(carrier.name)"
                      class="show-more-btn"
                    >
                      {{ showAllInsureds[carrier.name] ? 'Show Less' : `Show All ${carrier.insureds.length} Insureds` }}
                    </button>
                  </div>
                </div>

                <!-- Agents List -->
                <div v-if="carrier.agents && carrier.agents.length > 0" class="detail-section">
                  <h4>
                    <i class="fas fa-user-tie"></i>
                    Agents ({{ carrier.agents.length }})
                  </h4>
                  <div class="agents-list">
                    <div v-for="agent in carrier.agents" :key="agent.email || agent.name" class="agent-item">
                      <div class="agent-info">
                        <div class="agent-name">{{ agent.name }}</div>
                        <div class="agent-contact">{{ agent.email || agent.phone || 'No contact' }}</div>
                      </div>
                      <div class="agent-stats">
                        <span class="agent-policies">{{ agent.count }} policies</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Endorsements -->
                <div v-if="carrier.endorsements && carrier.endorsements.length > 0" class="detail-section">
                  <h4>
                    <i class="fas fa-file-signature"></i>
                    Common Endorsements
                  </h4>
                  <div class="endorsements-list">
                    <div v-for="endorsement in carrier.endorsements" :key="endorsement.code" class="endorsement-item">
                      <code class="endorsement-code">{{ endorsement.code }}</code>
                      <span class="endorsement-count">{{ endorsement.count }} occurrences</span>
                    </div>
                  </div>
                </div>

              </div>
            </transition>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredAndSortedCarriers.length === 0" class="empty-state">
          <i class="fas fa-search"></i>
          <h3>No carriers match your search</h3>
          <p>Try adjusting your search criteria</p>
        </div>

        <!-- Failed Extractions Section -->
        <div v-if="failedExtractions.length > 0" class="failed-extractions-section">
          <div class="section-header">
            <h3>
              <i class="fas fa-exclamation-triangle"></i>
              Failed Carrier Extractions ({{ failedExtractions.length }})
            </h3>
            <p class="section-description">
              These documents failed during carrier data extraction and need to be re-analyzed
            </p>
          </div>

          <!-- Error Summary -->
          <div class="error-summary">
            <h4>Error Breakdown:</h4>
            <div class="error-types">
              <div v-for="(count, errorType) in extractionErrors" :key="errorType" class="error-type-chip">
                <i class="fas fa-exclamation-circle"></i>
                <span class="error-type">{{ errorType }}</span>
                <span class="error-count">{{ count }}</span>
              </div>
            </div>
          </div>

          <!-- Failed Documents Table -->
          <div class="failed-docs-table">
            <table>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Analysis Date</th>
                  <th>Error Type</th>
                  <th>Error Details</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="doc in failedExtractions.slice(0, 50)" :key="doc.id" class="failed-doc-row">
                  <td class="doc-name">{{ doc.name }}</td>
                  <td class="doc-date">{{ new Date(doc.timestamp).toLocaleDateString() }}</td>
                  <td>
                    <span class="error-badge" :class="getErrorBadgeClass(doc.errorType)">
                      {{ doc.errorType }}
                    </span>
                  </td>
                  <td class="error-details">
                    <button @click="showErrorDetail(doc)" class="view-error-btn">
                      <i class="fas fa-info-circle"></i>
                      View Error
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="failedExtractions.length > 50" class="more-failures">
              + {{ failedExtractions.length - 50 }} more failed documents
            </div>
          </div>

          <!-- Recommendations -->
          <div class="extraction-recommendations">
            <h4><i class="fas fa-lightbulb"></i> Recommendations:</h4>
            <ul>
              <li v-if="extractionErrors['API Rate Limit (429)']">
                <strong>Rate Limit Issues ({{ extractionErrors['API Rate Limit (429)'] }} docs):</strong>
                Likely caused by parallel processing hitting Gemini API limits. Consider:
                <ul>
                  <li>Implementing batch processing with delays between batches</li>
                  <li>Using provider fallback (OpenAI/Claude when Gemini quota exceeded)</li>
                  <li>Upgrading Gemini API tier for higher quotas</li>
                  <li>Re-running analysis during off-peak hours</li>
                </ul>
              </li>
              <li v-if="extractionErrors['API Overloaded (503)']">
                <strong>API Overload ({{ extractionErrors['API Overloaded (503)'] }} docs):</strong>
                Gemini service was temporarily unavailable. These can be safely re-analyzed.
              </li>
              <li>
                <strong>Action:</strong> Use the RAG Analysis page to re-analyze these specific documents with corrected settings.
              </li>
            </ul>
          </div>
        </div>

      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loader">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading carrier analytics...</p>
      </div>
    </div>

    <!-- Empty State (No Data) -->
    <div v-if="!loading && results.length === 0 && !error" class="empty-state">
      <i class="fas fa-inbox"></i>
      <h3>No Data Available</h3>
      <p>Load a project to view carrier analytics</p>
      <p class="debug-info">
        Debug: loading={{ loading }}, results.length={{ results.length }}, error={{ error }}, projectId={{ projectId }}
      </p>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import { debounce } from 'lodash-es'
import { UI_CONSTANTS } from '@/constants'

export default {
  name: 'CarrierAnalytics',
  data() {
    return {
      projectId: '',
      projects: [],
      loadingProjects: false,
      loading: false,
      error: '',
      results: [],
      summary: {
        total_documents: 0,
        overall_compliance_rate: 0
      },
      carrierStats: {},
      expandedCarrier: null,
      showingDocs: {},
      showAllInsureds: {},
      searchQuery: '',
      sortBy: 'premium',
      sortOrder: 'desc',
      debouncedSearch: null,
      failedExtractions: [],
      extractionErrors: {},
      documentLookup: {}, // Map document_id to full document data with file_url
      entityStoreCounts: {} // Map entity names to their store counts from database
    }
  },
  computed: {
    totalPremium() {
      return Object.values(this.carrierStats).reduce((sum, c) => sum + c.totalPremium, 0)
    },
    averagePremium() {
      const carriers = Object.values(this.carrierStats)
      if (carriers.length === 0) return 0
      return this.totalPremium / this.summary.total_documents
    },
    totalLiabilityExposure() {
      // Sum of all aggregate limits across all carriers
      return Object.values(this.carrierStats).reduce((sum, c) => sum + (c.totalAggregate || 0), 0)
    },
    uniqueInsureds() {
      const allInsureds = new Set()
      this.results.forEach(r => {
        const ctx = r.document_context || {}
        const insured = ctx.key_entities?.insured || ctx.key_entities?.named_insured
        if (insured) {
          // Parse multiple insureds and add each one to the set
          const insuredNames = this._parseMultipleInsureds(insured)
          insuredNames.forEach(name => allInsureds.add(name))
        }
      })
      return allInsureds.size
    },
    uniqueStores() {
      // Aggregate stores from all carriers
      let totalStores = 0
      for (const carrier of Object.values(this.carrierStats)) {
        totalStores += carrier.uniqueStores || 0
      }
      return totalStores
    },
    filteredAndSortedCarriers() {
      let carriers = Object.entries(this.carrierStats).map(([name, stats]) => ({
        name,
        ...stats
      }))

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        carriers = carriers.filter(c => 
          c.name.toLowerCase().includes(query) ||
          (c.naic && c.naic.toString().includes(query)) ||
          (c.amBest && c.amBest.toLowerCase().includes(query))
        )
      }

      // Sort carriers
      carriers.sort((a, b) => {
        let aVal, bVal
        switch (this.sortBy) {
          case 'premium':
            aVal = a.totalPremium
            bVal = b.totalPremium
            break
          case 'policies':
            aVal = a.policyCount
            bVal = b.policyCount
            break
          case 'avgPremium':
            aVal = a.avgPremium
            bVal = b.avgPremium
            break
          case 'name':
          default:
            aVal = a.name.toLowerCase()
            bVal = b.name.toLowerCase()
            break
        }
        
        if (this.sortOrder === 'desc') {
          return bVal > aVal ? 1 : bVal < aVal ? -1 : 0
        } else {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
        }
      })

      return carriers
    }
  },
  watch: {
    searchQuery() {
      // Debouncing is already handled by lodash
      if (this.debouncedSearch) {
        this.debouncedSearch()
      }
    }
  },
  async mounted() {
    await this.fetchProjects()
    
    // Setup debounced search (not actively used but follows best practices)
    this.debouncedSearch = debounce(() => {
      // Search is reactive via computed property, this is just for future async searches
    }, UI_CONSTANTS.SEARCH_DEBOUNCE_MS)
  },
  methods: {
    async fetchProjects() {
      this.loadingProjects = true
      try {
        const response = await api.get('/projects/')
        
        // Handle different response structures
        let projectsData = []
        if (response && response.data) {
          if (Array.isArray(response.data.results)) {
            projectsData = response.data.results
          } else if (Array.isArray(response.data.projects)) {
            projectsData = response.data.projects
          } else if (Array.isArray(response.data)) {
            projectsData = response.data
          }
        }
        
        this.projects = projectsData.filter(p => !p.archived)
        
        // Auto-select project based on priority:
        // 1. Project 13 (Jersey Mike's - has most data)
        // 2. Project 68 (if exists)
        // 3. First project if only one
        // 4. First project otherwise
        if (this.projects.length === 1) {
          this.projectId = this.projects[0].id
        } else {
          // Default to project 13 (Jersey Mike's) if it exists (dev database)
          const project13 = this.projects.find(p => p.id === 13)
          if (project13) {
            this.projectId = 13
          } else {
            // Fallback to project 68 if it exists (production)
            const project68 = this.projects.find(p => p.id === 68)
            if (project68) {
              this.projectId = 68
            } else if (this.projects.length > 0) {
              // Otherwise default to first project
              this.projectId = this.projects[0].id
            }
          }
        }
      } catch (e) {
        console.error('Error fetching projects:', e)
        this.error = 'Failed to load projects: ' + (e.message || 'Unknown error')
      } finally {
        this.loadingProjects = false
      }
    },

    async loadResults() {
      if (!this.projectId) {
        this.error = 'Please select a project first'
        return
      }
      
      this.loading = true
      this.error = ''
      try {
        console.log(`[CarrierAnalytics] Loading results for project ${this.projectId}...`)
        
        // Try server-side aggregation first (faster for large datasets)
        try {
          console.log(`[CarrierAnalytics] Attempting server-side aggregation...`)
          const summaryRes = await api.get(`/projects/${this.projectId}/carrier-analytics/summary/`)
          
          if (summaryRes.data?.success) {
            console.log(`[CarrierAnalytics] Using server-side aggregation`)
            console.log(`[CarrierAnalytics] Summary:`, summaryRes.data.summary)
            
            // Use pre-computed data from server
            this.summary = summaryRes.data.summary
            this.failedExtractions = summaryRes.data.failed_extractions || []
            this.extractionErrors = summaryRes.data.extraction_errors || {}
            
            // Convert carriers array to object format for backward compatibility
            this.carrierStats = {}
            for (const carrier of (summaryRes.data.carriers || [])) {
              const name = carrier.name
              this.carrierStats[name] = carrier
            }
            
            // We don't have individual results in server-side mode
            // but we can still export data from carrierStats
            this.results = []
            
            console.log(`[CarrierAnalytics] Server-side aggregation complete. Carriers: ${Object.keys(this.carrierStats).length}`)
            return
          }
        } catch (serverErr) {
          console.warn(`[CarrierAnalytics] Server-side aggregation failed, falling back to client-side:`, serverErr)
        }
        
        // Fallback: Load all results and compute client-side (legacy behavior)
        console.log(`[CarrierAnalytics] Using client-side aggregation (fallback)...`)
        const [resultsRes, storeCountsRes] = await Promise.all([
          api.get(`/projects/${this.projectId}/rag/results/`),
          api.get('/entity-store-counts/').catch(err => {
            console.warn('[CarrierAnalytics] Could not load store counts:', err)
            return { data: { entity_store_counts: {} } }
          })
        ])
        
        console.log(`[CarrierAnalytics] API Response:`, resultsRes)
        console.log(`[CarrierAnalytics] Results count: ${resultsRes.data?.length || 0}`)
        console.log(`[CarrierAnalytics] Store counts loaded: ${Object.keys(storeCountsRes.data.entity_store_counts || {}).length} entities`)
        
        this.results = resultsRes.data || []
        this.entityStoreCounts = storeCountsRes.data.entity_store_counts || {}
        
        this.summary = {
          total_documents: this.results.length,
          overall_compliance_rate: this._avg(this.results.map(r => r.compliance_score || 0))
        }
        
        console.log(`[CarrierAnalytics] Summary:`, this.summary)
        
        // Build document lookup map for quick access to file_urls
        this.documentLookup = {}
        this.results.forEach(r => {
          if (r.document_id) {
            this.documentLookup[r.document_id] = {
              id: r.document_id,
              name: r.document_name,
              file_url: r.file_url,
              document_name: r.document_name
            }
          }
        })
        
        console.log(`[CarrierAnalytics] Building carrier stats (client-side)...`)
        this._buildCarrierStats()
        console.log(`[CarrierAnalytics] Carrier stats built. Carriers: ${Object.keys(this.carrierStats).length}`)
      } catch (e) {
        console.error(`[CarrierAnalytics] Error loading results:`, e)
        this.error = e?.response?.data?.error || e.message || 'Failed to load data'
      } finally {
        this.loading = false
      }
    },

    _buildCarrierStats() {
      const stats = {}
      const failed = []
      const errors = {}
      const seenPolicies = new Set()  // Track unique policies
      const naicToCarrier = {}  // Map NAIC numbers to canonical carrier names
      
      // First pass: aggregate carrier data
      for (const r of this.results) {
        const ctx = r.document_context || {}
        
        // Check for extraction failures
        const extractionError = ctx.extraction_metadata?.error
        if (extractionError) {
          failed.push({
            id: r.document_id,
            name: r.document_name,
            error: extractionError,
            timestamp: r.analysis_timestamp,
            errorType: this._categorizeError(extractionError)
          })
          
          // Track error types
          const errorType = this._categorizeError(extractionError)
          errors[errorType] = (errors[errorType] || 0) + 1
        }
        
        // Priority 1: Use carrier_details (LLM specifically extracted as carrier)
        let carrier = ctx.carrier_details?.carrier_name
        
        // Priority 2: Use key_entities.insurer (but validate it's not an agency)
        if (!carrier && ctx.key_entities?.insurer) {
          const insurer = ctx.key_entities.insurer
          const producer = ctx.key_entities?.producer || ''
          
          // Skip if:
          // - Insurer matches producer (same entity = agency)
          // - Insurer looks like an agency name
          if (insurer && insurer !== producer && !this._isLikelyAgency(insurer)) {
            carrier = insurer
          }
        }
        
        // Priority 3: Try to extract from structured_sections if available
        if (!carrier && ctx.structured_sections) {
          const insurerSection = ctx.structured_sections.insurer_section || 
                                ctx.structured_sections.coverages_section || ''
          // Look for "INSURER" label followed by company name
          const insurerMatch = insurerSection.match(/INSURER[:\s]+([A-Z][^\n]+)/i)
          if (insurerMatch && insurerMatch[1]) {
            const extracted = insurerMatch[1].trim()
            if (!this._isLikelyAgency(extracted)) {
              carrier = extracted
            }
          }
        }
        
        const naic = ctx.carrier_details?.naic_number
        const amBest = ctx.carrier_details?.am_best_rating
        
        // NAIC-based grouping: If we have a NAIC number, use it to ensure consistent carrier naming
        if (naic) {
          if (naicToCarrier[naic]) {
            // Use the existing canonical name for this NAIC
            carrier = naicToCarrier[naic]
          } else {
            // First time seeing this NAIC - normalize and store the canonical name
            carrier = carrier ? this._normalizeCarrierName(carrier) : 'Unknown Carrier'
            naicToCarrier[naic] = carrier
          }
        } else {
          // No NAIC - just normalize the name
          carrier = carrier ? this._normalizeCarrierName(carrier) : 'Unknown Carrier'
        }
        const premiumTotal = Number(ctx.premium_information?.total_premium || 0)
        const insured = ctx.key_entities?.insured || ctx.key_entities?.named_insured
        const rawPolicyTypes = ctx.coverage_types && Array.isArray(ctx.coverage_types) ? ctx.coverage_types : ['unknown']
        // Normalize policy types to handle variations (GL vs General Liability, etc.)
        const policyTypes = rawPolicyTypes.map(pt => this._normalizePolicyType(pt))
        const policyNumber = (ctx.policy_numbers && ctx.policy_numbers[0]) || ''
        
        // Create unique document identifier for deduplication at document level
        const docKey = `${carrier}|${naic || 'no-naic'}|${insured || 'no-insured'}|${policyNumber || r.document_name}`
        
        // Skip if we've already counted this exact document
        if (seenPolicies.has(docKey)) {
          continue  // Duplicate document, skip counting
        }
        seenPolicies.add(docKey)
        
        // Initialize carrier if not exists
        if (!stats[carrier]) {
          stats[carrier] = {
            policyCount: 0,
            totalPremium: 0,
            totalPerOccurrence: 0,
            totalAggregate: 0,
            naic: naic,
            amBest: amBest,
            policyTypes: {},
            insureds: {},
            agents: {},
            endorsements: {}
          }
        }

        const carrierData = stats[carrier]
        carrierData.policyCount += 1
        carrierData.totalPremium += isNaN(premiumTotal) ? 0 : premiumTotal
        
        // Track total liability exposure at carrier level
        const fin = ctx.financial_values || {}
        const perOccurrence = Number(fin.general_liability_limit || fin.each_occurrence || 0)
        const aggregate = Number(fin.aggregate_limit || 0)
        carrierData.totalPerOccurrence += isNaN(perOccurrence) ? 0 : perOccurrence
        carrierData.totalAggregate += isNaN(aggregate) ? 0 : aggregate
        
        // Update NAIC and AM Best if available
        if (!carrierData.naic && naic) carrierData.naic = naic
        if (!carrierData.amBest && amBest) carrierData.amBest = amBest

        // Track ALL policy types found in this document (not just the first one)
        for (const policyType of policyTypes) {
          if (!carrierData.policyTypes[policyType]) {
            carrierData.policyTypes[policyType] = {
              type: policyType,
              count: 0,
              totalPremium: 0,
              sumPerOccurrence: 0,
              sumAggregate: 0,
              documents: []
            }
          }
          
          const ptData = carrierData.policyTypes[policyType]
          ptData.count += 1
          // Divide premium across all policy types to avoid counting it multiple times
          ptData.totalPremium += premiumTotal / policyTypes.length
          ptData.sumPerOccurrence += (isNaN(perOccurrence) ? 0 : perOccurrence) / policyTypes.length
          ptData.sumAggregate += (isNaN(aggregate) ? 0 : aggregate) / policyTypes.length
          
          // Add document to this policy type
          ptData.documents.push({
            id: r.document_id,
            document_name: r.document_name,
            insured: insured,
            premium: premiumTotal,
            policy_number: (ctx.policy_numbers && ctx.policy_numbers[0]) || null
          })
        }

        // Track insureds - handle multiple insureds on same policy
        if (insured) {
          // Parse potentially multiple insureds (semicolon-delimited with DBA, C/O, etc.)
          const insuredNames = this._parseMultipleInsureds(insured)
          
          // Track each insured separately
          for (const insuredName of insuredNames) {
            if (!carrierData.insureds[insuredName]) {
              carrierData.insureds[insuredName] = { 
                name: insuredName, 
                count: 0, 
                totalPremium: 0,
                stores: new Set()
              }
            }
            carrierData.insureds[insuredName].count += 1
            // Divide premium across all insureds on this policy to avoid double-counting
            carrierData.insureds[insuredName].totalPremium += premiumTotal / insuredNames.length
            
            // Extract store numbers for this specific insured (uses database if available)
            const storeNumbers = this._extractStoreNumbers(ctx, insuredName)
            storeNumbers.forEach(store => carrierData.insureds[insuredName].stores.add(store))
          }
        }

        // Track agents
        const contacts = ctx.agent_contacts || []
        for (const contact of contacts) {
          const key = contact.email || contact.name || 'unknown'
          if (!carrierData.agents[key]) {
            carrierData.agents[key] = {
              name: contact.name,
              email: contact.email,
              phone: contact.phone,
              count: 0
            }
          }
          carrierData.agents[key].count += 1
        }

        // Track endorsements
        const endorsements = ctx.endorsements || []
        for (const endorsement of endorsements) {
          const code = endorsement.code || endorsement.description || 'UNKNOWN'
          if (!carrierData.endorsements[code]) {
            carrierData.endorsements[code] = { code: code, count: 0 }
          }
          carrierData.endorsements[code].count += 1
        }
      }

      // Second pass: calculate averages and format data
      for (const [, data] of Object.entries(stats)) {
        data.avgPremium = data.policyCount > 0 ? data.totalPremium / data.policyCount : 0
        data.avgPerOccurrence = data.policyCount > 0 ? data.totalPerOccurrence / data.policyCount : 0
        data.avgAggregate = data.policyCount > 0 ? data.totalAggregate / data.policyCount : 0
        
        // Convert policy types to array and calculate averages
        data.policyTypes = Object.values(data.policyTypes).map(pt => ({
          ...pt,
          avgPremium: pt.count > 0 ? pt.totalPremium / pt.count : 0,
          avgPerOccurrence: pt.count > 0 ? pt.sumPerOccurrence / pt.count : 0,
          avgAggregate: pt.count > 0 ? pt.sumAggregate / pt.count : 0,
          totalPerOccurrence: pt.sumPerOccurrence,
          totalAggregate: pt.sumAggregate
        })).sort((a, b) => b.totalPremium - a.totalPremium)

        // Convert insureds to sorted array and convert stores Set to array
        data.insureds = Object.values(data.insureds).map(insured => ({
          ...insured,
          stores: Array.from(insured.stores || []).sort(),
          storeCount: insured.stores ? insured.stores.size : 0
        })).sort((a, b) => b.totalPremium - a.totalPremium)

        // Convert agents to sorted array
        data.agents = Object.values(data.agents)
          .sort((a, b) => b.count - a.count)

        // Convert endorsements to sorted array
        data.endorsements = Object.values(data.endorsements)
          .sort((a, b) => b.count - a.count)

        data.uniqueInsureds = data.insureds.length
        data.uniqueStores = data.insureds.reduce((sum, i) => sum + (i.storeCount || 0), 0)
      }

      this.carrierStats = stats
      this.failedExtractions = failed
      this.extractionErrors = errors
    },

    toggleCarrier(carrierName) {
      this.expandedCarrier = this.expandedCarrier === carrierName ? null : carrierName
    },

    togglePolicyDocs(carrier, policyType) {
      const key = `${carrier}-${policyType}`
      this.showingDocs = {
        ...this.showingDocs,
        [key]: !this.showingDocs[key]
      }
    },

    toggleShowAllInsureds(carrier) {
      this.showAllInsureds = {
        ...this.showAllInsureds,
        [carrier]: !this.showAllInsureds[carrier]
      }
    },

    openDocument(documentId) {
      if (!documentId) {
        console.error('No document ID provided')
        return
      }

      const doc = this.documentLookup[documentId]
      if (!doc) {
        console.error('Document not found in lookup:', documentId)
        this.$toast?.error('Document not found')
        return
      }

      const url = this.getDocumentUrl(doc)
      if (url && url !== '#') {
        console.log('Opening document URL:', url)
        window.open(url, '_blank')
      } else {
        console.error('Could not generate document URL for:', doc)
        this.$toast?.error('Unable to view document - file not found')
      }
    },

    getDocumentUrl(doc) {
      // Use unified document URL helper
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(doc);
    },

    isPDF(doc) {
      // Use unified PDF detection
      const { isPDF } = require('@/utils/documentUrlHelper');
      return isPDF(doc);
    },

    formatPolicyType(type) {
      if (!type) return 'Unknown'
      return type.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ')
    },

    formatCurrency(n) {
      if (!n || isNaN(n)) return '$0'
      try {
        return new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(n)
      } catch (_) {
        return `$${Number(n).toFixed(0)}`
      }
    },

    _avg(arr) {
      if (!arr.length) return 0
      return arr.reduce((a, b) => a + b, 0) / arr.length
    },
    
    _categorizeError(errorText) {
      if (!errorText) return 'Unknown'
      const errorStr = errorText.toLowerCase()
      
      if (errorStr.includes('429') || errorStr.includes('quota') || errorStr.includes('rate limit')) {
        return 'API Rate Limit (429)'
      } else if (errorStr.includes('503') || errorStr.includes('overloaded')) {
        return 'API Overloaded (503)'
      } else if (errorStr.includes('500') || errorStr.includes('internal error')) {
        return 'API Internal Error (500)'
      } else if (errorStr.includes('timeout')) {
        return 'Timeout'
      } else if (errorStr.includes('authentication') || errorStr.includes('401')) {
        return 'Authentication Error'
      } else {
        return 'Other Error'
      }
    },
    
    _isLikelyAgency(name) {
      // Check if name contains common agency/broker keywords
      const agencyKeywords = [
        'agency', 'agencies', 'broker', 'brokerage', 'insurance group',
        'insurance services', 'risk management', 'insurance solutions',
        'insurance advisors', 'insurance consultants', 'insurance partners',
        'risk services', 'benefit services'
      ]
      const lowerName = name.toLowerCase()
      return agencyKeywords.some(keyword => lowerName.includes(keyword))
    },
    
    _extractStoreNumbers(documentContext, insuredName = null) {
      /**
       * Extract store/location numbers for an insured.
       * 
       * Phase 1: Use imported Location data from database (most reliable)
       * Phase 2: Parse from document text as fallback
       * 
       * @param {Object} documentContext - The document context from analysis
       * @param {String} insuredName - The normalized insured name to match against entities
       * @returns {Array} Array of unique store numbers
       */
      const storeNumbers = []
      
      // Phase 1: Use database store counts (most reliable)
      if (insuredName && this.entityStoreCounts) {
        // Try exact match first
        if (this.entityStoreCounts[insuredName]) {
          return this.entityStoreCounts[insuredName].store_numbers || []
        }
        
        // Try case-insensitive match
        const normalizedInsured = insuredName.toLowerCase()
        for (const [entityName, entityData] of Object.entries(this.entityStoreCounts)) {
          if (entityName.toLowerCase() === normalizedInsured) {
            return entityData.store_numbers || []
          }
        }
      }
      
      // Phase 2: Fallback to text parsing (for insureds not in database)
      const textFields = [
        documentContext.structured_sections?.description_of_operations || '',
        documentContext.structured_sections?.insured_section || '',
        documentContext.key_entities?.insured || '',
        documentContext.key_entities?.named_insured || ''
      ]
      
      // Pattern: Store #12345, Store #1234A, Location #5678, #9999, etc.
      const patterns = [
        /(?:store|location)\s*#?\s*(\d+[A-Z]?)/gi,  // "Store #5003" or "Store 5003A"
        /#(\d+[A-Z]?)\b/g,  // "#5003" or "#5003A"
        /\bstore\s+(\d+[A-Z]?)\b/gi,  // "Store 3200" or "STORE 5003A"
        /\b(\d{4,5}[A-Z]?)\b/g  // Standalone 4-5 digit numbers (like "5003", "1012A")
      ]
      
      for (const text of textFields) {
        if (!text) continue
        
        for (const pattern of patterns) {
          const matches = text.matchAll(pattern)
          for (const match of matches) {
            const storeNum = match[1].toUpperCase().trim()
            // Only include if it looks like a store number (4-5 digits, optional letter)
            if (/^\d{4,5}[A-Z]?$/.test(storeNum)) {
              storeNumbers.push(storeNum)
            }
          }
        }
      }
      
      // Return unique store numbers
      return [...new Set(storeNumbers)]
    },
    
    _parseMultipleInsureds(insuredString) {
      /**
       * Parse a potentially complex insured string that may contain multiple entities.
       * Handles formats like:
       * - "Company A; Company B; Company C"
       * - "Company A DBA: Brand Name, Company B, Company C C/O Manager"
       * - "Company A & Company B"
       * 
       * Returns an array of individual insured names.
       */
      if (!insuredString || typeof insuredString !== 'string') {
        return []
      }
      
      // First, split by semicolons (primary delimiter for multiple insureds)
      const parts = insuredString.split(';').map(p => p.trim()).filter(p => p.length > 0)
      
      if (parts.length === 1) {
        // No semicolons found, check for other patterns
        const singlePart = parts[0]
        
        // Check for comma-separated list (but be careful not to split addresses)
        // Only split on commas if there are multiple company indicators (Inc., LLC, Corp., etc.)
        const companyIndicatorPattern = /\b(Inc\.?|LLC|L\.L\.C\.|Corp\.?|Corporation|Ltd\.?|Limited|Co\.?|Company|DBA|d\/b\/a)\b/gi
        const matches = singlePart.match(companyIndicatorPattern)
        
        if (matches && matches.length >= 2) {
          // Likely multiple companies separated by commas
          // Split by comma, but try to preserve "Company, Inc" patterns
          const commaParts = singlePart.split(',').map(p => p.trim()).filter(p => p.length > 0)
          
          // Merge back parts that are just entity types (Inc, LLC, etc.)
          const mergedParts = []
          let currentPart = ''
          
          for (let i = 0; i < commaParts.length; i++) {
            const part = commaParts[i]
            
            // Check if this part is ONLY an entity type (Inc, LLC, etc.) without a company name
            const isOnlyEntityType = /^(Inc\.?|LLC|L\.L\.C\.|Corp\.?|Corporation|Ltd\.?|Limited|Co\.?|Company)$/i.test(part)
            
            if (isOnlyEntityType && currentPart) {
              // Merge with previous part
              currentPart += ', ' + part
            } else {
              // Save previous part if exists
              if (currentPart) {
                mergedParts.push(this._cleanInsuredName(currentPart))
              }
              currentPart = part
            }
          }
          
          // Don't forget the last part
          if (currentPart) {
            mergedParts.push(this._cleanInsuredName(currentPart))
          }
          
          if (mergedParts.length > 1) {
            return mergedParts
          }
        }
        
        // Single insured, just clean and return
        return [this._cleanInsuredName(singlePart)]
      }
      
      // Multiple parts separated by semicolons - clean each one
      return parts.map(p => this._cleanInsuredName(p)).filter(p => p.length > 0)
    },
    
    _cleanInsuredName(name) {
      /**
       * Clean up an individual insured name by:
       * - Removing C/O, DBA, and similar prefixes
       * - Normalizing case to Title Case for consistency
       * - Trimming whitespace
       * - Preserving the core company name
       */
      if (!name) return ''
      
      let cleaned = name.trim()
      
      // Remove "C/O" or "c/o" at the end (often used for "in care of")
      cleaned = cleaned.replace(/\s+C\/O\s+.+$/i, '')
      
      // For DBA (doing business as), keep both parts but format nicely
      // Example: "Jersey Mike's Sub Shop DBA: Mapes Restaurant Group" -> "Jersey Mike's Sub Shop (DBA Mapes Restaurant Group)"
      const dbaMatch = cleaned.match(/^(.+?)\s+DBA:?\s+(.+)$/i)
      if (dbaMatch) {
        const [, primary, dba] = dbaMatch
        // Apply title case to both parts
        cleaned = `${this._toTitleCase(primary.trim())} (DBA ${this._toTitleCase(dba.trim())})`
      } else {
        // Apply title case to the whole name for consistency
        // This prevents "JERSEY MIKE'S" and "Jersey Mike's" from being separate insureds
        cleaned = this._toTitleCase(cleaned)
      }
      
      return cleaned.trim()
    },
    
    _normalizeCarrierName(name) {
      /**
       * Normalize carrier names to handle variations in capitalization and formatting.
       * 
       * Strategy:
       * 1. Check against known carrier patterns for consistent branding
       * 2. Apply title case for unknown carriers (looks better than UPPERCASE or lowercase)
       * 3. Preserve acronyms and special cases (LLC, Inc., etc.)
       * 
       * @param {string} name - The carrier name to normalize
       * @returns {string} Normalized carrier name
       */
      if (!name) return 'Unknown Carrier'
      
      // Common carrier normalization patterns - maps variations to canonical brand names
      const normalizations = {
        // Hartford variations
        'The Hartford': /(?:the\s+)?hartford(?:\s+insurance)?(?:\s+company)?(?:\s+of\s+midwest)?/i,
        
        // Travelers variations  
        'Travelers': /travelers(?:\s+insurance)?(?:\s+companies)?(?:\s+inc\.?)?/i,
        
        // Liberty Mutual variations
        'Liberty Mutual': /liberty\s+mutual(?:\s+insurance)?/i,
        
        // State Farm variations
        'State Farm': /state\s+farm(?:\s+mutual)?(?:\s+insurance)?/i,
        
        // Progressive variations
        'Progressive': /progressive(?:\s+insurance)?(?:\s+commercial)?/i,
        
        // Nationwide variations
        'Nationwide': /nationwide(?:\s+insurance)?(?:\s+mutual)?/i,
        
        // Farmers variations
        'Farmers': /farmers(?:\s+insurance)?(?:\s+group)?/i,
        
        // AIG variations
        'AIG': /(?:aig|american\s+international\s+group)(?:\s+insurance)?/i,
        
        // CNA variations
        'CNA': /(?:cna|continental\s+casualty)(?:\s+company)?/i,
        
        // Zurich variations
        'Zurich': /zurich(?:\s+insurance)?(?:\s+north\s+america)?/i,
        
        // Chubb variations
        'Chubb': /(?:chubb|federal\s+insurance\s+company)/i,
        
        // Valley Forge
        'Valley Forge Insurance': /valley\s+forge\s+insurance/i,
        
        // U.S. Specialty
        'U.S. Specialty Insurance': /u\.?s\.?\s+specialty\s+insurance/i,
        
        // Acuity
        'Acuity': /acuity(?:\s+insurance)?/i,
        
        // Auto-Owners
        'Auto-Owners Insurance': /auto[\s-]owners(?:\s+insurance)?/i,
        
        // Cincinnati
        'Cincinnati Insurance': /cincinnati(?:\s+insurance)?(?:\s+company)?/i,
        
        // Great American
        'Great American': /great\s+american(?:\s+insurance)?/i,
        
        // Hanover
        'The Hanover': /(?:the\s+)?hanover(?:\s+insurance)?/i,
        
        // IMT
        'IMT Insurance': /imt(?:\s+insurance)?/i,
        
        // EMPLOYERS
        'EMPLOYERS': /employers(?:\s+insurance)?(?:\s+company)?/i,
        
        // Markel
        'Markel': /markel(?:\s+insurance)?/i,
        
        // PHI
        'Philadelphia Insurance': /(?:philadelphia|philly)(?:\s+insurance)?/i,
        
        // Old Republic
        'Old Republic': /old\s+republic(?:\s+insurance)?/i,
        
        // Scottsdale
        'Scottsdale Insurance': /scottsdale(?:\s+insurance)?/i,
        
        // Selective
        'Selective Insurance': /selective(?:\s+insurance)?/i,
        
        // Society
        'Society Insurance': /society(?:\s+insurance)?/i,
        
        // W.R. Berkley
        'W.R. Berkley': /w\.?\s?r\.?\s?berkley/i,
        
        // AmTrust
        'AmTrust': /amtrust(?:\s+insurance)?/i,
        
        // Berkshire Hathaway
        'Berkshire Hathaway': /berkshire\s+hathaway(?:\s+insurance)?/i
      }
      
      const trimmedName = name.trim()
      const lowerName = trimmedName.toLowerCase()
      
      // Check against normalization patterns
      for (const [canonical, pattern] of Object.entries(normalizations)) {
        if (pattern.test(lowerName)) {
          return canonical
        }
      }
      
      // If no pattern match, apply intelligent title case
      return this._toTitleCase(trimmedName)
    },
    
    _normalizePolicyType(policyType) {
      /**
       * Normalize policy type names to handle common variations and abbreviations.
       * 
       * Handles:
       * - Case variations: "General Liability", "GENERAL LIABILITY", "general liability"
       * - Abbreviations: "GL", "CGL", "WC", "AL", "EPLI"
       * - Common misspellings: "Workers Comp", "Worker's Compensation"
       * - Underscores vs spaces: "general_liability", "general liability"
       * 
       * @param {string} policyType - The raw policy type from extraction
       * @returns {string} Normalized policy type name
       */
      if (!policyType || typeof policyType !== 'string') {
        return 'Unknown'
      }
      
      // Normalize: trim, lowercase, replace underscores with spaces
      const normalized = policyType.trim().toLowerCase().replace(/_/g, ' ')
      
      // Policy type normalization mappings (variations → canonical name)
      const policyMappings = {
        // General Liability
        'General Liability': /^(general\s+liability|gl|cgl|commercial\s+general\s+liability|general\s+liab)$/i,
        
        // Workers Compensation - ENHANCED with employers_liability variations
        'Workers Compensation': /^(workers?\s+comp(ensation)?|wc|work\s+comp|workers\s+comp|worker\s+comp|workers\s+compensation|workers?\s+comp(ensation)?\s+(and\s+)?employers?\s+liability|employers?\s+liability|occupational\s+disease)$/i,
        
        // Auto Liability - ENHANCED to catch hired/non-owned variations
        'Auto Liability': /^(auto\s+liability|automobile\s+liability|auto|automobile|al|business\s+auto|commercial\s+auto|any\s+auto|hired\s+non\s+owned\s+auto|auto\s+hired\s+non\s+owned|auto\s+non\s+owned|auto\s+hired\s+nonowned)$/i,
        
        // Professional Liability
        'Professional Liability': /^(professional\s+liability|prof\s+liability|e\s*&\s*o|errors\s+and\s+omissions|pl|professional)$/i,
        
        // Umbrella - ENHANCED to catch liab variations
        'Umbrella': /^(umbrella|commercial\s+umbrella|umbrella\s+liab(ility)?|umbrella\s+excess|excess[\s/]umbrella)$/i,
        
        // Excess Liability
        'Excess Liability': /^(excess\s+liability|excess\s+liab|excess)$/i,
        
        // Property - ENHANCED to catch BPP, tenant improvements, business income
        'Property': /^(property|commercial\s+property|building\s+property|bpp|business\s+personal\s+property|b\.?p\.?p\.?|tenant\s+improvements?|tenants?\s+improvements?|tib|business\s+income|business\s+prop(erty)?|property\s+coverage|contents|bbp\s+tenant\s+improv|bpp\s*&\s*ti[bn]|damage\s+to\s+rented\s+premises|plate\s+glass)$/i,
        
        // Cyber Liability - ENHANCED
        'Cyber Liability': /^(cyber\s+liability|cyber|data\s+breach|cyber\s+security|network\s+security|internet\s+security(\s+and\s+privacy)?|online\s+banking\s+theft)$/i,
        
        // Employment Practices Liability - MASSIVELY ENHANCED to catch all variations
        'Employment Practices Liability': /^(employment\s+practices?\s+liability|epli?|e\.?p\.?l\.?i?|employment\s+liability|employers?\s+practices?|emp(loyment)?\s+practices?|employee?\s+practices?\s+liability|employ(ee|mt|er)?\s+prac(tices?|s)?(\s+liab(ility)?)?|epl\s+incls\s+third\s+party|fair\s+labor\s+standards\s+act)$/i,
        
        // Directors & Officers
        'Directors & Officers': /^(directors?\s+and\s+officers?|d\s*&\s*o|d\s+and\s+o|directors\s+officers|do)$/i,
        
        // Crime/Fidelity
        'Crime': /^(crime|fidelity|employee\s+dishonesty|crime\s+coverage)$/i,
        
        // Pollution Liability
        'Pollution Liability': /^(pollution\s+liability|environmental\s+liability|pollution|environmental)$/i,
        
        // Liquor Liability
        'Liquor Liability': /^(liquor\s+liability|liquor|dram\s+shop|foodborne\s+illness)$/i,
        
        // Products Liability - ENHANCED
        'Products Liability': /^(products?\s+liability|product\s+liab|products?\s+completed\s+operations|prod\s+liab|personal\s+and\s+advertising\s+injury)$/i,
        
        // Inland Marine
        'Inland Marine': /^(inland\s+marine|marine|im|equipment)$/i,
        
        // Garage Liability
        'Garage Liability': /^(garage\s+liability|garage\s+keepers|garagekeepers|garage)$/i,
        
        // Builders Risk
        'Builders Risk': /^(builders?\s+risk|construction\s+risk|installation\s+floater)$/i,
        
        // Medical Malpractice  
        'Medical Malpractice': /^(medical\s+malpractice|med\s+mal|malpractice|medical\s+professional|practice\s+liability)$/i,
        
        // Bonds
        'Surety Bonds': /^(surety\s+bonds?|bonds?|performance\s+bond|payment\s+bond)$/i,
        
        // Equipment Breakdown
        'Equipment Breakdown': /^(equipment\s+breakdown|boiler\s+and\s+machinery|mechanical\s+breakdown)$/i,
        
        // Commercial Package (BOP)
        'Commercial Package': /^(commercial\s+package|package\s+policy|bop|businessowners?|business\s+owners?(\s+policy)?)$/i,
        
        // Employee Benefits Liability
        'Employee Benefits Liability': /^(employee\s+benefits?\s+liability|disability)$/i
      }
      
      // Check against normalization patterns
      for (const [canonical, pattern] of Object.entries(policyMappings)) {
        if (pattern.test(normalized)) {
          return canonical
        }
      }
      
      // If no pattern match, apply title case to make it presentable
      return this._toTitleCase(policyType)
    },
    
    _toTitleCase(str) {
      /**
       * Convert a string to title case while preserving acronyms and special patterns.
       * Examples:
       * - "HARTFORD INSURANCE" → "Hartford Insurance"
       * - "the hartford" → "The Hartford"
       * - "ABC INSURANCE CO LLC" → "ABC Insurance Co LLC"
       */
      if (!str) return ''
      
      // List of words to keep lowercase (unless they're the first word)
      const lowercaseWords = new Set(['of', 'the', 'and', 'or', 'in', 'on', 'at', 'to', 'for', 'by', 'with'])
      
      // List of abbreviations to keep uppercase
      const uppercaseWords = new Set(['llc', 'inc', 'co', 'corp', 'ltd', 'lp', 'dba', 'usa', 'us', 'uk', 'na'])
      
      // Split into words and process each
      const words = str.trim().split(/\s+/)
      
      return words.map((word, index) => {
        const lowerWord = word.toLowerCase()
        
        // Check if it's an acronym (all caps and short) or known abbreviation
        if (uppercaseWords.has(lowerWord)) {
          return word.toUpperCase()
        }
        
        // Check if it's a word that should stay lowercase (except at start)
        if (index > 0 && lowercaseWords.has(lowerWord)) {
          return lowerWord
        }
        
        // Check if it appears to be an acronym (2-4 chars, originally all caps)
        if (word.length <= 4 && word === word.toUpperCase() && /^[A-Z]+$/.test(word)) {
          return word  // Keep as-is (likely acronym like "CNA", "AIG")
        }
        
        // Standard title case: capitalize first letter, lowercase the rest
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      }).join(' ')
    },

    exportData() {
      // Build CSV data
      const rows = []
      rows.push([
        'Carrier', 
        'NAIC', 
        'A.M. Best', 
        'Policy Count', 
        'Total Premium', 
        'Avg Premium', 
        'Total Aggregate Limit', 
        'Avg Aggregate Limit',
        'Total Per Occurrence',
        'Avg Per Occurrence',
        'Unique Insureds', 
        'Policy Type', 
        'Type Count', 
        'Type Premium',
        'Type Total Aggregate',
        'Type Avg Aggregate',
        'Type Total Per Occ',
        'Type Avg Per Occ'
      ])
      
      for (const [carrier, data] of Object.entries(this.carrierStats)) {
        for (const pt of data.policyTypes) {
          rows.push([
            carrier,
            data.naic || '',
            data.amBest || '',
            data.policyCount,
            data.totalPremium,
            data.avgPremium,
            data.totalAggregate || 0,
            data.avgAggregate || 0,
            data.totalPerOccurrence || 0,
            data.avgPerOccurrence || 0,
            data.uniqueInsureds,
            pt.type,
            pt.count,
            pt.totalPremium,
            pt.totalAggregate || 0,
            pt.avgAggregate || 0,
            pt.totalPerOccurrence || 0,
            pt.avgPerOccurrence || 0
          ])
        }
      }

      // Convert to CSV
      const csv = rows.map(row => row.map(cell => 
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(',')).join('\n')

      // Download
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `carrier-analytics-${this.projectId}-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    },
    
    exportMissingCertificates() {
      /**
       * Export a list of entities and stores that have NO certificates in the analyzed documents.
       * Shows the coverage gap - which stores/companies need to submit insurance documentation.
       */
      
      // Get all insureds found in certificates (normalized)
      const insuredsInCertificates = new Set()
      for (const r of this.results) {
        const ctx = r.document_context || {}
        const insured = ctx.key_entities?.insured || ctx.key_entities?.named_insured
        if (insured) {
          const insuredNames = this._parseMultipleInsureds(insured)
          insuredNames.forEach(name => insuredsInCertificates.add(name))
        }
      }
      
      console.log(`[MissingCerts] Found ${insuredsInCertificates.size} unique insureds in certificates`)
      console.log(`[MissingCerts] Have ${Object.keys(this.entityStoreCounts).length} entities with stores in database`)
      
      // Find entities with stores but NO certificates
      const missingEntities = []
      for (const [entityName, entityData] of Object.entries(this.entityStoreCounts)) {
        // Check if this entity appears in any certificate
        const hasMatch = insuredsInCertificates.has(entityName)
        
        // Also try case-insensitive match
        let found = hasMatch
        if (!found) {
          const normalizedEntity = entityName.toLowerCase()
          for (const insuredName of insuredsInCertificates) {
            if (insuredName.toLowerCase() === normalizedEntity) {
              found = true
              break
            }
          }
        }
        
        if (!found) {
          // This entity has stores but no certificates!
          missingEntities.push({
            entity_name: entityName,
            store_count: entityData.location_count,
            store_numbers: entityData.store_numbers || []
          })
        }
      }
      
      console.log(`[MissingCerts] Found ${missingEntities.length} entities with no certificates`)
      
      // Build CSV
      const rows = []
      rows.push(['Company Name', 'Store Count', 'Store Numbers (Missing Certificates)', 'First Store', 'Last Store'])
      
      // Sort by store count (most stores first - highest priority)
      missingEntities.sort((a, b) => b.store_count - a.store_count)
      
      for (const entity of missingEntities) {
        const stores = entity.store_numbers.sort()
        rows.push([
          entity.entity_name,
          entity.store_count,
          stores.join(', '),
          stores[0] || '',
          stores[stores.length - 1] || ''
        ])
      }
      
      // Add summary
      rows.push([])
      rows.push(['=== SUMMARY ==='])
      rows.push(['Total Entities Missing Certificates', missingEntities.length])
      rows.push(['Total Stores Missing Certificates', missingEntities.reduce((sum, e) => sum + e.store_count, 0)])
      rows.push(['Total Entities WITH Certificates', Object.keys(this.entityStoreCounts).length - missingEntities.length])
      rows.push(['Coverage Rate', `${((1 - missingEntities.length / Object.keys(this.entityStoreCounts).length) * 100).toFixed(1)}%`])
      
      // Convert to CSV
      const csv = rows.map(row => row.map(cell => 
        typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
          ? `"${cell.replace(/"/g, '""')}"` 
          : cell
      ).join(',')).join('\n')
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `missing-certificates-${this.projectId}-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      
      // Also show alert with summary
      alert(`Missing Certificates Report:\n\n${missingEntities.length} companies with ${missingEntities.reduce((sum, e) => sum + e.store_count, 0)} stores have NO certificates.\n\nCSV downloaded with details.`)
    },
    
    exportPolicyTypeDiagnostic() {
      /**
       * Diagnostic export showing:
       * 1. All unique ORIGINAL (raw) policy type values from data
       * 2. What they normalize to
       * 3. How many times each appears
       * 
       * Use this to identify patterns that need to be added to _normalizePolicyType()
       */
      
      const policyTypeMapping = {}
      
      // Collect all raw policy types and their normalized versions
      for (const r of this.results) {
        const ctx = r.document_context || {}
        const rawPolicyTypes = ctx.coverage_types && Array.isArray(ctx.coverage_types) ? ctx.coverage_types : []
        
        for (const rawType of rawPolicyTypes) {
          if (!rawType) continue
          
          const normalized = this._normalizePolicyType(rawType)
          
          if (!policyTypeMapping[rawType]) {
            policyTypeMapping[rawType] = {
              raw: rawType,
              normalized: normalized,
              count: 0
            }
          }
          policyTypeMapping[rawType].count++
        }
      }
      
      // Build CSV
      const rows = []
      rows.push(['Raw Value (From Data)', 'Normalized To', 'Count', 'Needs Pattern?'])
      
      // Sort by count (most common first)
      const sorted = Object.values(policyTypeMapping).sort((a, b) => b.count - a.count)
      
      for (const item of sorted) {
        // Flag if raw and normalized are different (good) or same (might need pattern)
        const needsPattern = item.raw === item.normalized ? 'Maybe' : 'OK'
        
        rows.push([
          item.raw,
          item.normalized,
          item.count,
          needsPattern
        ])
      }
      
      // Add summary stats at the bottom
      rows.push([])
      rows.push(['=== SUMMARY ==='])
      rows.push(['Unique Raw Values', Object.keys(policyTypeMapping).length])
      rows.push(['Unique Normalized Values', new Set(Object.values(policyTypeMapping).map(v => v.normalized)).size])
      rows.push(['Consolidation Ratio', `${Object.keys(policyTypeMapping).length} → ${new Set(Object.values(policyTypeMapping).map(v => v.normalized)).size}`])
      
      // Convert to CSV
      const csv = rows.map(row => row.map(cell => 
        typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
          ? `"${cell.replace(/"/g, '""')}"` 
          : cell
      ).join(',')).join('\n')
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `policy-type-diagnostic-${this.projectId}-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      
      // Also log to console for quick reference
      console.log('📊 Policy Type Diagnostic Report:')
      console.log(`Total raw values: ${Object.keys(policyTypeMapping).length}`)
      console.log(`Normalized to: ${new Set(Object.values(policyTypeMapping).map(v => v.normalized)).size}`)
      console.log('\nTop 20 most common:')
      console.table(sorted.slice(0, 20))
    },
    
    exportInsuredMatrix() {
      /**
       * Export a matrix view of insureds with policy types as columns.
       * Each row = unique insured
       * Columns = policy types + expiration dates
       * Shows which coverages each insured has
       */
      
      // Build insured-centric data structure
      const insuredMatrix = {}
      
      // Get all unique policy types across all data
      const allPolicyTypes = new Set()
      
      // First pass: collect all insureds and their policies
      for (const r of this.results) {
        const ctx = r.document_context || {}
        const insured = ctx.key_entities?.insured || ctx.key_entities?.named_insured
        if (!insured) continue
        
        // Parse multiple insureds
        const insuredNames = this._parseMultipleInsureds(insured)
        const rawPolicyTypes = ctx.coverage_types && Array.isArray(ctx.coverage_types) ? ctx.coverage_types : []
        const policyTypes = rawPolicyTypes.map(pt => this._normalizePolicyType(pt))
        
        // Get expiration date
        const expirationDate = ctx.dates?.expiration_date || ctx.dates?.effective_date || null
        
        // Get carrier info
        let carrier = ctx.carrier_details?.carrier_name || ctx.key_entities?.insurer
        if (!carrier && ctx.key_entities?.insurer) {
          carrier = ctx.key_entities.insurer
        }
        carrier = carrier ? this._normalizeCarrierName(carrier) : 'Unknown'
        
        // Extract store numbers
        const storeNumbers = this._extractStoreNumbers(ctx)
        
        // Track each insured
        for (const insuredName of insuredNames) {
          if (!insuredMatrix[insuredName]) {
            insuredMatrix[insuredName] = {
              name: insuredName,
              policyTypes: {},
              carriers: new Set(),
              stores: new Set()
            }
          }
          
          // Track carrier
          insuredMatrix[insuredName].carriers.add(carrier)
          
          // Track store numbers
          storeNumbers.forEach(store => insuredMatrix[insuredName].stores.add(store))
          
          // Track each policy type with its expiration date
          for (const policyType of policyTypes) {
            allPolicyTypes.add(policyType)
            
            if (!insuredMatrix[insuredName].policyTypes[policyType]) {
              insuredMatrix[insuredName].policyTypes[policyType] = {
                hasPolicy: true,
                expirationDates: [],
                carriers: new Set()
              }
            }
            
            if (expirationDate) {
              insuredMatrix[insuredName].policyTypes[policyType].expirationDates.push(expirationDate)
            }
            insuredMatrix[insuredName].policyTypes[policyType].carriers.add(carrier)
          }
        }
      }
      
      // Sort policy types for consistent column order
      const sortedPolicyTypes = Array.from(allPolicyTypes).sort()
      
      // Build CSV header
      const headers = [
        'Insured Name',
        'Store Count',
        'Store Numbers',
        'Carriers',
        ...sortedPolicyTypes.map(pt => `${pt} - Has Coverage`),
        ...sortedPolicyTypes.map(pt => `${pt} - Expiration Date`),
        ...sortedPolicyTypes.map(pt => `${pt} - Carrier`)
      ]
      
      const rows = [headers]
      
      // Build rows for each insured
      const sortedInsureds = Object.values(insuredMatrix).sort((a, b) => 
        a.name.localeCompare(b.name)
      )
      
      for (const insured of sortedInsureds) {
        const row = []
        
        // Insured name
        row.push(insured.name)
        
        // Store count and numbers
        const stores = Array.from(insured.stores || []).sort()
        row.push(stores.length)
        row.push(stores.join(', '))
        
        // Carriers (comma-separated)
        row.push(Array.from(insured.carriers).sort().join(', '))
        
        // For each policy type: Has Coverage column
        for (const policyType of sortedPolicyTypes) {
          const policyData = insured.policyTypes[policyType]
          row.push(policyData ? 'Yes' : 'No')
        }
        
        // For each policy type: Expiration Date column (most relevant date)
        for (const policyType of sortedPolicyTypes) {
          const policyData = insured.policyTypes[policyType]
          if (!policyData || !policyData.expirationDates.length) {
            row.push('')
            continue
          }
          
          // Get the most relevant expiration date
          // Filter out invalid dates first
          const validDates = policyData.expirationDates
            .filter(d => d && d !== '')
            .map(d => new Date(d))
            .filter(d => !isNaN(d.getTime()))  // Remove Invalid Date objects
          
          if (validDates.length === 0) {
            row.push('')
            continue
          }
          
          const today = new Date()
          
          // Separate into future and past dates
          const futureDates = validDates.filter(d => d >= today)
          const pastDates = validDates.filter(d => d < today)
          
          let relevantDate
          if (futureDates.length > 0) {
            // Use furthest future date
            relevantDate = new Date(Math.max(...futureDates))
          } else if (pastDates.length > 0) {
            // Use most recent past date
            relevantDate = new Date(Math.max(...pastDates))
          } else {
            relevantDate = validDates[0]
          }
          
          row.push(relevantDate.toISOString().slice(0, 10))
        }
        
        // For each policy type: Carrier column
        for (const policyType of sortedPolicyTypes) {
          const policyData = insured.policyTypes[policyType]
          if (!policyData) {
            row.push('')
          } else {
            row.push(Array.from(policyData.carriers).sort().join(', '))
          }
        }
        
        rows.push(row)
      }
      
      // Convert to CSV
      const csv = rows.map(row => row.map(cell => 
        typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
          ? `"${cell.replace(/"/g, '""')}"` 
          : cell
      ).join(',')).join('\n')
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `insured-coverage-matrix-${this.projectId}-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    },
    
    getErrorBadgeClass(errorType) {
      if (errorType.includes('429')) return 'error-rate-limit'
      if (errorType.includes('503')) return 'error-overload'
      if (errorType.includes('500')) return 'error-server'
      return 'error-other'
    },
    
    showErrorDetail(doc) {
      alert(`Error Details for: ${doc.name}\n\nError Type: ${doc.errorType}\n\nFull Error:\n${doc.error.substring(0, 500)}...`)
    }
  }
}
</script>

<style scoped>
.carrier-analytics {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding-bottom: 3rem;
}

.header-section {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.main-title {
  color: white;
  font-size: 2.25rem;
  margin: 0 0 0.5rem 0;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.subtitle {
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1.5rem;
  font-size: 1.1rem;
}

.controls {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.project-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.project-selector label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.project-selector label i {
  color: #667eea;
}

.project-select {
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: white;
  font-size: 0.95rem;
  min-width: 250px;
  cursor: pointer;
  transition: all 0.2s;
}

.project-select:hover:not(:disabled) {
  border-color: #667eea;
}

.project-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.project-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f3f4f6;
}

.project-input {
  padding: 0.65rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.95);
  min-width: 140px;
  font-size: 1rem;
}

.load-btn, .export-btn {
  background: #2563eb;
  color: white;
  border: none;
  padding: 0.65rem 1.25rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.load-btn:hover, .export-btn:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.load-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.export-btn {
  background: #10b981;
}

.export-btn:hover {
  background: #059669;
}

.notification.error {
  background: #fee2e2;
  color: #991b1b;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #fecaca;
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.dashboard-section {
  padding: 2rem 0;
}

.metrics-bar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.metric-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: bold;
  color: #1f2937;
}

.metric-label {
  color: #6b7280;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.filter-section {
  background: rgba(255, 255, 255, 0.95);
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 250px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.search-input {
  width: 100%;
  padding: 0.65rem 1rem 0.65rem 2.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
}

.filter-controls {
  display: flex;
  gap: 0.75rem;
}

.sort-select {
  padding: 0.65rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 0.95rem;
}

.carriers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.carrier-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.carrier-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.carrier-card.expanded {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
}

.carrier-header {
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-bottom: 2px solid #e5e7eb;
  transition: background 0.2s;
}

.carrier-header:hover {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}

.carrier-info {
  flex: 1;
}

.carrier-name {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.carrier-meta {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.meta-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.meta-badge.rating {
  background: #fef3c7;
  color: #92400e;
}

.carrier-summary {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.summary-stat {
  text-align: center;
  min-width: 90px;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
}

.stat-label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.expand-icon {
  color: #6b7280;
  font-size: 1.25rem;
}

.carrier-details {
  padding: 1.5rem;
  background: #fafafa;
  border-top: 1px solid #e5e7eb;
}

.detail-section {
  margin-bottom: 2rem;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h4 {
  font-size: 1.15rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.policy-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1rem;
}

.policy-type-card {
  background: white;
  border-radius: 10px;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.policy-type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #f3f4f6;
}

.policy-type-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
}

.policy-type-count {
  color: #6b7280;
  font-size: 0.9rem;
}

.policy-type-stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
}

.stat-row .stat-label {
  color: #6b7280;
}

.stat-row .stat-value {
  font-weight: 600;
  color: #1f2937;
}

.policy-documents {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.toggle-docs-btn {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.toggle-docs-btn:hover {
  background: #e5e7eb;
}

.documents-list {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.document-item {
  background: #f9fafb;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .document-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .doc-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

.doc-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.doc-info i {
  color: #ef4444;
}

.doc-name {
  font-size: 0.9rem;
  color: #1f2937;
}

.doc-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.85rem;
  flex-wrap: wrap;
}

.insured-name {
  color: #6b7280;
}

.premium-amt {
  color: #059669;
  font-weight: 600;
}

.policy-num {
  color: #9ca3af;
  font-family: monospace;
}

.doc-actions {
  display: flex;
  gap: 0.5rem;
}

.open-doc-btn {
  padding: 0.4rem 0.75rem;
  border: 1px solid #667eea;
  background: white;
  color: #667eea;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
}

.open-doc-btn:hover:not(:disabled) {
  background: #667eea;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.open-doc-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.open-doc-btn i {
  font-size: 0.8rem;
}

.insureds-list, .agents-list, .endorsements-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.insured-item, .agent-item {
  background: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.insured-name, .agent-name {
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.store-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.store-badge::before {
  content: '🏪';
  font-size: 0.85rem;
}

.insured-stats, .agent-stats {
  display: flex;
  gap: 1rem;
  font-size: 0.9rem;
}

.insured-policies, .agent-policies {
  color: #6b7280;
}

.insured-premium {
  color: #059669;
  font-weight: 600;
}

.agent-item {
  display: flex;
  justify-content: space-between;
}

.agent-info {
  flex: 1;
}

.agent-contact {
  color: #6b7280;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.endorsements-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.5rem;
}

.endorsement-item {
  background: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.endorsement-code {
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.85rem;
}

.endorsement-count {
  color: #6b7280;
  font-size: 0.9rem;
}

.show-more-btn {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  transition: all 0.2s;
}

.show-more-btn:hover {
  background: #dbeafe;
}

.empty-state, .loading-state {
  text-align: center;
  padding: 4rem 2rem;
  color: white;
}

.empty-state i, .loading-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3, .loading-state h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.loader {
  display: inline-block;
}

.loader i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

/* Slide transition */
.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.slide-enter-from, .slide-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .carrier-summary {
    gap: 1rem;
  }
  
  .summary-stat {
    min-width: 70px;
  }
  
  .stat-value {
    font-size: 1.1rem;
  }
}

@media (max-width: 768px) {
  .main-title {
    font-size: 1.75rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .carrier-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .carrier-summary {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  
  .policy-types-grid {
    grid-template-columns: 1fr;
  }
  
  .metrics-bar {
    grid-template-columns: 1fr;
  }
  
  .filter-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    width: 100%;
  }
  
  .filter-controls {
    width: 100%;
    flex-direction: column;
  }
  
  .sort-select {
    width: 100%;
  }
}

/* Failed Extractions Section */
.failed-extractions-section {
  margin-top: 3rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 2rem;
  border: 2px solid #fbbf24;
}

.failed-extractions-section .section-header h3 {
  color: #92400e;
  font-size: 1.5rem;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.failed-extractions-section .section-description {
  color: #78350f;
  margin: 0 0 1.5rem 0;
}

.error-summary {
  background: #fef3c7;
  padding: 1.25rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.error-summary h4 {
  margin: 0 0 1rem 0;
  color: #92400e;
}

.error-types {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.error-type-chip {
  background: white;
  border: 1px solid #fbbf24;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-type-chip i {
  color: #d97706;
}

.error-type {
  font-weight: 600;
  color: #92400e;
}

.error-count {
  background: #fbbf24;
  color: white;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
}

.failed-docs-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.failed-docs-table table {
  width: 100%;
  border-collapse: collapse;
}

.failed-docs-table th {
  background: #f59e0b;
  color: white;
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
}

.failed-docs-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.failed-doc-row:hover {
  background: #fef3c7;
}

.error-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
}

.error-badge.error-rate-limit {
  background: #fee2e2;
  color: #991b1b;
}

.error-badge.error-overload {
  background: #fed7aa;
  color: #9a3412;
}

.error-badge.error-server {
  background: #fecaca;
  color: #7f1d1d;
}

.error-badge.error-other {
  background: #e5e7eb;
  color: #374151;
}

.view-error-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  transition: background 0.2s;
}

.view-error-btn:hover {
  background: #2563eb;
}

.more-failures {
  padding: 1rem;
  text-align: center;
  color: #92400e;
  font-weight: 600;
  background: #fef3c7;
}

.extraction-recommendations {
  background: #dbeafe;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.extraction-recommendations h4 {
  margin: 0 0 1rem 0;
  color: #1e40af;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.extraction-recommendations ul {
  margin: 0;
  padding-left: 1.5rem;
}

.extraction-recommendations li {
  margin: 0.75rem 0;
  color: #1e3a8a;
}

.extraction-recommendations li ul {
  margin-top: 0.5rem;
}

.metric-warning {
  background: rgba(255, 237, 213, 0.95);
  border: 2px solid #f59e0b;
}

.metric-warning .metric-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.metric-warning .metric-value {
  color: #92400e;
}
</style>

